import React, { useState, useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../shared/api/api.js';
import { 
  X, 
  Save, 
  Send, 
  Eye, 
  Settings, 
  FileText,
  AlertCircle,
  CheckCircle2,
  Loader2,
  ChevronRight,
  Info,
  Sparkles,
  Maximize2,
  Minimize2,
  Tag,
  Layers,
  ChevronDown
} from 'lucide-react';
import { 
  RichTextEditor, 
  type LineSpacingOption, 
  type ParaSpacingOption 
} from '../../../shared/components/RichTextEditor.js';
import { type Post, type BlogCategory } from '../api/blogApi.js';
import { getCategoryBadgeStyle, getCategoryDotStyle } from './CategoryManager.js';
import { createPortal } from 'react-dom';
import { useNotificationStore } from '../../notifications/store/useNotificationStore.js';

interface PostEditorProps {
  post: Post | null;
  isOpen: boolean;
  onClose: () => void;
}

export const PostEditor: React.FC<PostEditorProps> = ({ post, isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const showToast = useNotificationStore(state => state.showToast);

  const { data: categories } = useQuery({
    queryKey: ['blog-categories'],
    queryFn: () => api.getBlogCategories(),
  });
  
  const [title, setTitle] = useState(post?.title || '');
  const [subtitle, setSubtitle] = useState(post?.subtitle || '');
  const [content, setContent] = useState(post?.content || '');
  const [summary, setSummary] = useState(post?.summary || '');
  const [authorBio, setAuthorBio] = useState(post?.authorBio || '');
  const [authorRole, setAuthorRole] = useState(post?.authorRole || '');
  const [status, setStatus] = useState<'draft' | 'published'>(post?.status || 'draft');
  const [lineSpacing, setLineSpacing] = useState<LineSpacingOption>(post?.lineSpacing || 'normal');
  const [paraSpacing, setParaSpacing] = useState<ParaSpacingOption>(post?.paraSpacing || 'normal');
  const [tags, setTags] = useState(post?.tags || '');
  const [category, setCategory] = useState(post?.category || '');
  const [categoryId, setCategoryId] = useState(post?.categoryId || '');
  const [isFeatured, setIsFeatured] = useState(post?.isFeatured === 1);
  const [activeTab, setActiveTab] = useState<'editor' | 'settings'>('editor');
  const [pendingStatus, setPendingStatus] = useState<'draft' | 'published' | null>(null);
  const [isFocusMode, setIsFocusMode] = useState(false);

  const titleRef = useRef<HTMLTextAreaElement>(null);
  const subtitleRef = useRef<HTMLTextAreaElement>(null);

  // Resize Textareas dynamically to fit title and subtitle on all screen sizes (mobile word wrap)
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = 'auto';
      titleRef.current.style.height = `${titleRef.current.scrollHeight}px`;
    }
  }, [title, isOpen, activeTab]);

  useEffect(() => {
    if (subtitleRef.current) {
      subtitleRef.current.style.height = 'auto';
      subtitleRef.current.style.height = `${subtitleRef.current.scrollHeight}px`;
    }
  }, [subtitle, isOpen, activeTab]);

  useEffect(() => {
    if (post) {
      setTitle(post.title || '');
      setSubtitle(post.subtitle || '');
      setContent(post.content || '');
      setSummary(post.summary || '');
      setAuthorBio(post.authorBio || '');
      setAuthorRole(post.authorRole || '');
      setStatus(post.status || 'draft');
      setLineSpacing(post.lineSpacing || 'normal');
      setParaSpacing(post.paraSpacing || 'normal');
      setTags(post.tags || '');
      setCategory(post.category || '');
      setCategoryId(post.categoryId || '');
      setIsFeatured(post.isFeatured === 1);
    } else {
      setTitle('');
      setSubtitle('');
      setContent('');
      setSummary('');
      setAuthorBio('');
      setAuthorRole('');
      setStatus('draft');
      setLineSpacing('normal');
      setParaSpacing('normal');
      setTags('');
      setCategory('');
      setCategoryId('');
      setIsFeatured(false);
    }
  }, [post]);

  const handleSelectCategory = (catNameOrId: string) => {
    if (!catNameOrId || catNameOrId === 'none') {
      setCategory('');
      setCategoryId('');
      return;
    }
    const matched = categories?.find(c => c.id === catNameOrId || c.name === catNameOrId);
    if (matched) {
      setCategoryId(matched.id);
      setCategory(matched.name);
    } else {
      setCategory(catNameOrId);
      setCategoryId('');
    }
  };

  const mutation = useMutation({
    mutationFn: (data: Partial<Post>) => {
      if (post) {
        return api.updatePost(post.id, data);
      }
      return api.createPost(data);
    },
    onSuccess: (savedPost) => {
      queryClient.invalidateQueries({ queryKey: ['admin-blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] });
      queryClient.invalidateQueries({ queryKey: ['blog-categories'] });
      showToast(
        post ? 'Artikel Diperbarui' : 'Artikel Dibuat',
        `Artikel "${savedPost.title}" berhasil ${post ? 'diperbarui' : 'dibuat'}.`,
        'success'
      );
      onClose();
    },
    onError: (error: any) => {
      showToast('Gagal Menyimpan', error.message || 'Terjadi kesalahan saat menyimpan artikel.', 'error');
    },
    onSettled: () => {
      setPendingStatus(null);
    }
  });

  const handleAutoGenerateTags = () => {
    const textToAnalyze = `${title} ${content}`.toLowerCase();
    const tagsSet = new Set<string>();

    const keywordRules = [
      { keywords: ['uibn', 'nomor uibn', 'identifikasi', 'standar'], tags: ['UIBN', 'Identifikasi Buku', 'Standarisasi'] },
      { keywords: ['penulisan', 'menulis', 'karya', 'naskah', 'ide', 'plot', 'karakter'], tags: ['Tips Menulis', 'Kepenulisan', 'Proses Kreatif'] },
      { keywords: ['penerbit', 'penerbitan', 'cetak', 'distribusi', 'penerbit indie', 'redaksi'], tags: ['Penerbitan', 'Industri Buku', 'Publikasi'] },
      { keywords: ['isbn', 'barcode', 'qr code', 'katalog', 'verifikasi'], tags: ['ISBN', 'Katalog Buku', 'Verifikasi Buku'] },
      { keywords: ['literasi', 'membaca', 'minat baca', 'perpustakaan', 'buku'], tags: ['Literasi', 'Membaca', 'Budaya Baca'] },
      { keywords: ['digital', 'ebook', 'pdf', 'epub', 'aplikasi', 'online'], tags: ['E-Book', 'Literasi Digital', 'Teknologi'] },
      { keywords: ['indie', 'mandiri', 'self-publish'], tags: ['Penulis Mandiri', 'Indie Publishing'] },
      { keywords: ['regulasi', 'legal', 'hukum', 'hak cipta', 'paten', 'cipta', 'undang-undang'], tags: ['Regulasi', 'Hak Cipta', 'Legalitas Buku'] },
      { keywords: ['desain', 'cover', 'sampul', 'layout', 'ilustrasi'], tags: ['Desain Buku', 'Sampul Buku', 'Layout'] },
      { keywords: ['novel', 'fiksi', 'cerita', 'puisi', 'sastra'], tags: ['Fiksi', 'Karya Sastra', 'Sastra Indonesia'] },
      { keywords: ['sejarah', 'biografi', 'nonfiksi', 'ilmiah', 'penelitian', 'jurnal'], tags: ['Nonfiksi', 'Edukasi', 'Ilmiah'] }
    ];

    for (const rule of keywordRules) {
      if (rule.keywords.some(keyword => textToAnalyze.includes(keyword))) {
        rule.tags.forEach(t => tagsSet.add(t));
      }
    }

    const generated = tagsSet.size > 0 ? Array.from(tagsSet).slice(0, 5).join(', ') : 'Buku Nusantara, Literasi, Edukasi';
    setTags(generated);
    showToast('Tag Dihasilkan', 'Tag artikel berhasil dibuat otomatis berdasarkan isi konten Anda.', 'success');
  };

  const handleSave = (newStatus?: 'draft' | 'published') => {
    if (!title.trim() || !content.trim()) {
      showToast('Data Tidak Lengkap', 'Judul dan konten wajib diisi.', 'warning');
      return;
    }

    const targetStatus = newStatus || status;
    setPendingStatus(targetStatus);
    
    mutation.mutate({
      title,
      subtitle,
      content,
      summary,
      authorBio,
      authorRole,
      status: targetStatus,
      lineSpacing,
      paraSpacing,
      tags,
      category: category || null,
      categoryId: categoryId || null,
      isFeatured: isFeatured ? 1 : 0,
    });
  };

  const editorContent = (
    <div className={`fixed inset-0 z-50 flex items-center justify-center transition-all duration-300 ${isFocusMode ? 'p-0 bg-white' : 'p-0 sm:p-4 bg-slate-900/60 backdrop-blur-xs'}`}>
      <div className={`bg-white h-full flex flex-col overflow-hidden transition-all duration-300 ${isFocusMode ? 'w-full border-none shadow-none' : 'w-full max-w-6xl sm:h-[90dvh] sm:rounded-3xl shadow-2xl border border-slate-200'}`}>
        {/* Header */}
        <div className={`px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between shrink-0 gap-3 transition-colors duration-300 ${isFocusMode ? 'bg-amber-50/10' : 'bg-white'}`}>
          <div className="flex items-center justify-between md:justify-start gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl bg-indigo-600 text-white flex items-center justify-center shadow-lg shadow-indigo-500/20">
                <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
              <div>
                <h2 className="text-sm sm:text-lg font-bold text-slate-900 truncate max-w-[150px] sm:max-w-none">
                  {post ? 'Edit Artikel' : 'Tulis Artikel Baru'}
                </h2>
                <div className="flex items-center gap-1 sm:gap-2 text-[9px] sm:text-[10px] text-slate-400 font-bold uppercase tracking-wider">
                  <span className="hidden xs:inline">Draf Penulisan</span>
                  <ChevronRight className="w-3 h-3 hidden xs:inline" />
                  <span className={status === 'published' ? 'text-emerald-500' : 'text-amber-500'}>
                    {status === 'published' ? 'Siap Terbit' : 'Konsep'}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Close button for mobile inside the first row if we want to save space */}
            <button 
              onClick={onClose}
              className="md:hidden p-2 rounded-xl text-slate-400 hover:bg-slate-100 transition-all"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-between md:justify-end gap-2 w-full md:w-auto">
            <div className="flex items-center gap-1 sm:gap-2 mr-1 sm:mr-4 md:border-r md:border-slate-100 md:pr-4">
              <button 
                onClick={() => setActiveTab('editor')}
                className={`px-2 sm:px-3 py-1.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-bold transition-all cursor-pointer ${
                  activeTab === 'editor' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                Editor
              </button>
              <button 
                onClick={() => setActiveTab('settings')}
                className={`px-2 sm:px-3 py-1.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-bold transition-all cursor-pointer ${
                  activeTab === 'settings' ? 'bg-indigo-50 text-indigo-700' : 'text-slate-400 hover:text-slate-600'
                }`}
              >
                <span className="hidden xs:inline">Pengaturan SEO</span>
                <span className="xs:hidden">SEO</span>
              </button>
              <button 
                type="button"
                onClick={() => setIsFocusMode(!isFocusMode)}
                className={`px-2 sm:px-3 py-1.5 rounded-lg sm:rounded-xl text-[10px] sm:text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                  isFocusMode 
                    ? 'bg-amber-500 text-white shadow-md hover:bg-amber-600' 
                    : 'bg-amber-50 text-amber-700 hover:bg-amber-100'
                }`}
                title="Mode Fokus Menulis (Bebas Distraksi)"
              >
                <Sparkles className={`w-3.5 h-3.5 ${isFocusMode ? 'animate-pulse' : ''}`} />
                <span className="hidden xs:inline">{isFocusMode ? 'Keluar Fokus' : 'Mode Fokus'}</span>
                <span className="xs:hidden">Fokus</span>
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button 
                onClick={() => handleSave('draft')}
                disabled={mutation.isPending}
                className="px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl border border-slate-200 text-slate-600 font-bold text-[10px] sm:text-xs hover:bg-slate-50 transition-all flex items-center gap-1.5 sm:gap-2"
              >
                {mutation.isPending && pendingStatus === 'draft' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">Simpan Draf</span>
                <span className="sm:hidden">Draf</span>
              </button>
              
              <button 
                onClick={() => handleSave('published')}
                disabled={mutation.isPending}
                className="px-3 sm:px-5 py-2 rounded-lg sm:rounded-xl bg-indigo-600 text-white font-bold text-[10px] sm:text-xs hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-500/20 flex items-center gap-1.5 sm:gap-2"
              >
                {mutation.isPending && pendingStatus === 'published' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">Terbitkan</span>
                <span className="sm:hidden">Terbit</span>
              </button>

              <button 
                onClick={onClose}
                className="hidden md:flex p-2 rounded-xl text-slate-400 hover:bg-slate-100 transition-all ml-2"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content Area */}
        <div className={`flex-1 overflow-y-auto transition-colors duration-300 ${isFocusMode ? 'bg-[#FAF8F5]' : 'bg-slate-50/50'}`}>
          <div className={`mx-auto px-6 py-8 transition-all duration-300 ${isFocusMode ? 'max-w-3xl' : 'max-w-4xl'}`}>
            {isFocusMode && activeTab === 'editor' && (
              <div className="mb-6 p-4 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-between text-xs text-amber-900 animate-fade-in shrink-0">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
                  <span className="font-bold">Mode Fokus Aktif:</span>
                  <span className="font-medium text-slate-600">Seluruh distraksi disembunyikan. Toolbar penulisan di bawah akan tetap sticky di bagian atas saat digulir.</span>
                </div>
                <button 
                  type="button" 
                  onClick={() => setIsFocusMode(false)}
                  className="text-amber-700 font-bold hover:underline cursor-pointer"
                >
                  Keluar Mode Fokus
                </button>
              </div>
            )}

            {activeTab === 'editor' ? (
              <div className="space-y-6">
                {/* Category Selector Bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
                      <Layers className="w-3.5 h-3.5 text-indigo-500" />
                      Kategori Artikel:
                    </span>
                    <select
                      value={categoryId || category || 'none'}
                      onChange={(e) => handleSelectCategory(e.target.value)}
                      className="px-3 py-1.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl text-xs font-bold text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all cursor-pointer"
                    >
                      <option value="none">-- Pilih Kategori Artikel --</option>
                      {categories?.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    {category && (
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border text-[11px] font-bold ${getCategoryBadgeStyle(
                          categories?.find((c) => c.id === categoryId || c.name === category)?.color
                        )}`}
                      >
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${getCategoryDotStyle(
                            categories?.find((c) => c.id === categoryId || c.name === category)?.color
                          )}`}
                        />
                        {category}
                      </span>
                    )}
                  </div>
                </div>

                {/* Title Input */}
                <div className="space-y-1 flex flex-col">
                  <textarea 
                    ref={titleRef}
                    placeholder="Masukkan Judul Artikel yang Menarik..." 
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    rows={1}
                    className="w-full bg-transparent border-none text-3xl sm:text-4xl font-extrabold text-slate-900 placeholder:text-slate-300 focus:ring-0 px-0 pt-2 pb-0 leading-tight outline-none resize-none overflow-hidden"
                  />
                  <textarea 
                    ref={subtitleRef}
                    placeholder="Tambah subjudul yang menjelaskan konten lebih dalam..." 
                    value={subtitle}
                    onChange={(e) => setSubtitle(e.target.value)}
                    rows={1}
                    className="w-full bg-transparent border-none text-lg sm:text-xl font-medium text-slate-500 placeholder:text-slate-300 focus:ring-0 px-0 pb-2 leading-tight outline-none resize-none overflow-hidden"
                  />
                </div>

                {/* Rich Text Editor */}
                <div className="min-h-[500px] sm:min-h-[600px]">
                  <RichTextEditor 
                    content={content} 
                    onChange={setContent} 
                    lineSpacing={lineSpacing}
                    paraSpacing={paraSpacing}
                    onChangeLineSpacing={setLineSpacing}
                    onChangeParaSpacing={setParaSpacing}
                    placeholder="Mulai tuliskan wawasan Anda di sini..."
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
                <div className="bg-white p-6 rounded-3xl border border-slate-200 space-y-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 text-indigo-600">
                      <Settings className="w-5 h-5" />
                      <h3 className="font-bold">Pengaturan SEO & Ringkasan</h3>
                    </div>
                    
                    <button 
                      onClick={() => handleSave()}
                      disabled={mutation.isPending}
                      className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-500/10 cursor-pointer"
                    >
                      {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                      <span>Simpan Perubahan</span>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div className="p-4 rounded-2xl border border-indigo-100 bg-indigo-50/30 flex items-center justify-between">
                      <div className="space-y-1 pr-4">
                        <p className="text-xs font-bold text-indigo-950 uppercase tracking-wider">Sematkan sebagai Artikel Utama (Featured)</p>
                        <p className="text-[11px] text-slate-500">Artikel utama akan disematkan secara khusus di bagian atas halaman utama blog dengan visual kartu pahlawan (*hero banner*).</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={isFeatured}
                          onChange={(e) => setIsFeatured(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Pilih Kategori Artikel</label>
                      <select 
                        value={categoryId || category || 'none'}
                        onChange={(e) => handleSelectCategory(e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none cursor-pointer"
                      >
                        <option value="none">-- Tanpa Kategori (Umum) --</option>
                        {categories?.map((cat) => (
                          <option key={cat.id} value={cat.id}>
                            {cat.name} ({cat.slug})
                          </option>
                        ))}
                      </select>
                      <p className="text-[10px] text-slate-400 mt-1">Mengelompokkan artikel mempermudah pembaca menemukan tulisan terkait di portal blog UIBN.</p>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider">Tags / Topik Artikel</label>
                        <button
                          type="button"
                          onClick={handleAutoGenerateTags}
                          className="text-[11px] font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 cursor-pointer"
                        >
                          <Sparkles className="w-3 h-3 text-indigo-500" />
                          <span>Generate Tag Otomatis</span>
                        </button>
                      </div>
                      <input 
                        type="text"
                        placeholder="Contoh: UIBN, Kepenulisan, Standarisasi Buku (pisahkan dengan koma)"
                        value={tags}
                        onChange={(e) => setTags(e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Sistem akan secara cerdas memetakan topik konten jika Anda mengeklik tombol "Generate Tag Otomatis".</p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Ringkasan Artikel (Excerpt)</label>
                      <textarea 
                        rows={3}
                        placeholder="Berikan ringkasan singkat artikel ini untuk ditampilkan pada daftar blog. Maksimal 160 karakter untuk hasil SEO terbaik."
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none resize-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Posisi/Jabatan Penulis (Author Role)</label>
                      <input 
                        type="text"
                        placeholder="Contoh: Editor & Kontributor Konten UIBN"
                        value={authorRole}
                        onChange={(e) => setAuthorRole(e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Tentang Penulis (Author Bio)</label>
                      <textarea 
                        rows={3}
                        placeholder="Ceritakan sedikit tentang penulis artikel ini. Informasi ini akan muncul di bawah artikel."
                        value={authorBio}
                        onChange={(e) => setAuthorBio(e.target.value)}
                        className="w-full p-4 bg-slate-50 border border-slate-200 rounded-2xl text-sm focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all outline-none resize-none"
                      />
                    </div>

                    <div className="p-4 rounded-2xl bg-indigo-50 border border-indigo-100 flex gap-4">
                      <div className="w-10 h-10 rounded-xl bg-white text-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                        <Info className="w-5 h-5" />
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm font-bold text-indigo-900">Tips Penulisan Blog</p>
                        <p className="text-xs text-indigo-700 leading-relaxed">
                          Gunakan heading (H1, H2) secara terstruktur untuk memudahkan pembaca dan mesin pencari (SEO). Masukkan kata kunci seperti "penulisan buku", "registrasi UIBN", atau "penerbitan" untuk jangkauan lebih luas.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-3xl border border-slate-200">
                  <div className="flex items-center gap-3 text-slate-900 mb-6">
                    <Eye className="w-5 h-5" />
                    <h3 className="font-bold">Pratinjau Hasil Pencarian</h3>
                  </div>

                  <div className="p-5 border border-slate-100 rounded-2xl bg-white shadow-sm space-y-1.5">
                    <p className="text-[11px] text-slate-500 truncate">https://uibn.id/blog/{post?.slug || 'slug-otomatis'}</p>
                    <p className="text-lg font-bold text-blue-600 truncate">{title || 'Judul Artikel Anda Akan Muncul di Sini'}</p>
                    <p className="text-xs text-slate-600 line-clamp-2 leading-relaxed">
                      {summary || 'Ringkasan artikel Anda akan muncul di sini sebagai deskripsi hasil pencarian di Google dan media sosial.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Status Bar */}
        <div className="px-6 py-3 border-t border-slate-100 bg-white flex items-center justify-between shrink-0 text-[11px] font-medium text-slate-400 uppercase tracking-widest">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse" />
              Sesi Menulis Aktif
            </span>
            <span className="hidden sm:block border-l border-slate-200 pl-4">
              Karakter: {content.length}
            </span>
            <span className="hidden md:block border-l border-slate-200 pl-4">
              Kata: {content.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(w => w.length > 0).length}
            </span>
            <span className="hidden lg:block border-l border-slate-200 pl-4">
              Estimasi: {Math.max(1, Math.ceil(content.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(w => w.length > 0).length / 225))} m
            </span>
          </div>
          <div>
            Terakhir disimpan: {new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(editorContent, document.body);
};
